/**
 * Actycrea: iframe-viewer.esm.js (ES6)
 * MIT
 */
import { PREFIX , CLOSE_FRAME } from '../helpers/config.js';
import { addQuery } from '../helpers/helpers.js';

const SELECTOR_ROOT   = `[data-${PREFIX}-myif]`;
const SELECTOR_LINK   = `[data-${PREFIX}-link]`;
const CLASS_OVERLAY   = 'iframe-overlay';
const CLASS_OPEN_BODY = 'if-open';

export class MyIF {
  /**
   * @param {HTMLElement} root
   */
  constructor( root ){
    if( !(root instanceof HTMLElement) ) throw new Error( 'MyIF: root inválido' );

    this._root      = root;
    this._overlay   = null;
    this._container = null;
    this._iframe    = null;
    this._lastState = null;

    this._bound     = {
      onClick : ( ev ) => this._onClick( ev ),
      onPop   : ( ev ) => this._onPopState( ev ),
      onMsg   : ( ev ) => this._onMessage( ev ),
      onKey   : ( ev ) => this._onKey( ev ),
      onLoad  : ( ) => this._onLoad( ),
    };

    this._root.addEventListener( 'click' , this._bound.onClick , false );
    window.addEventListener( 'popstate' , this._bound.onPop , false );
    window.addEventListener( 'message' , this._bound.onMsg , false );
    window.addEventListener( 'keydown' , this._bound.onKey , false );
  }

  _onClick( ev ){
    const a = ev.target.closest( SELECTOR_LINK );
    if( !a ) return;
    const href = a.getAttribute( 'href' );
    if( !href || href.startsWith( 'javascript:' ) ) return;

    ev.preventDefault();
    this.open( href );
  }

  open( href ){
    // pushState con URL del post
    this._lastState = window.location.href;
    const newUrl = href;
    window.history.pushState( { ifv: 1 , url: newUrl } , '' , newUrl );

    // crear overlay + iframe
    if( !this._overlay ){
      this._overlay = document.createElement( 'div' );
      this._overlay.className = CLASS_OVERLAY;

      // sentry focus para evitar tab al body
      const sentry = document.createElement( 'span' );
      sentry.className = `${CLASS_OVERLAY}-sentry`;
      sentry.tabIndex = 0;
      this._overlay.appendChild( sentry );

      this._container = document.createElement( 'div' );
      this._container.className = `${CLASS_OVERLAY}-container`;

      this._iframe = document.createElement( 'iframe' );
      this._iframe.className = `${CLASS_OVERLAY}-frame`;
      this._iframe.setAttribute( 'loading' , 'eager' );
      this._iframe.addEventListener( 'load' , this._bound.onLoad , false );
      this._container.appendChild( this._iframe );

      this._overlay.appendChild( this._container );
      document.body.appendChild( this._overlay );

    }

    document.documentElement.classList.add( CLASS_OPEN_BODY );

    // estado de carga
    this._container.classList.remove( 'is-load' );
    this._iframe.setAttribute( 'aria-busy' , 'true' );
    this._iframe.src = addQuery( href , 'frame' , 1 );

    // abre
    requestAnimationFrame( () => this._overlay.classList.add( 'is-open' ) );
  }

  close( ){
    if( !this._overlay ) return;

    // paso 1: eliminar estado de carga
    if( this._iframe ){
      this._container.classList.remove( 'is-load' );
      this._iframe.removeAttribute( 'aria-busy' );
    }

    // paso 2: retrasar cierre y limpieza
    setTimeout( () => {
      this._teardown( );
    } , 500 );
  }

  _onLoad( ){
    if( !this._iframe ) return;
    try {
      if( !this._overlay?.classList.contains( 'is-open' ) ) return;
      this._container.classList.add( 'is-load' );
      this._iframe.removeAttribute( 'aria-busy' );
    } catch( _ ) { /* noop */ }
  }

  _onPopState( ev ){
    if( this._overlay && this._overlay.classList.contains( 'is-open' ) ){
      this.close();
    }
  }

  _onMessage( ev ){
    console.log( '_onMessage => ' , ev , window.location.origin , ev?.data , CLOSE_FRAME , window.history.state?.ifv);
    if( ev.origin !== window.location.origin ) return;
    if( ev?.data?.type === CLOSE_FRAME ){
      const target = typeof ev?.data?.href === 'string' ? ev.data.href : '';
      if( target ){
        // cerrar ya y navegar en el parent al destino del enlace
        this._teardown( );
        try {
          if( window.history.state?.ifv === 1 ){
            window.history.back( );
          }
          window.location.assign( target );
        } catch ( _ ) { /* noop */ }
        return;
      }
      // cierre “normal” (botón cerrar / Escape)
      if( window.history.state?.ifv === 1 ){
        window.history.back( );
      } else {
        this.close( );
      }
    }
  }

  _teardown( ){
    if( !this._overlay ) return;
    this._overlay.classList.remove( 'is-open' );
    document.documentElement.classList.remove( CLASS_OPEN_BODY );
    if( this._iframe ){
      this._container?.classList.remove( 'is-load' );
      this._iframe.removeAttribute( 'aria-busy' );
      this._iframe.src = 'about:blank';
    }
  }

  _onKey( ev ){
    if( ev.key === 'Escape' && this._overlay?.classList.contains( 'is-open' ) ){
      if( window.history.state?.ifv === 1 ){
        window.history.back();
      } else {
        this.close();
      }
    }
  }

  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_ROOT );
    const instances = [ ];
    nodes.forEach( el => {
      if( el._myifInstance ) return;
      el._myifInstance = new MyIF( el );
      instances.push( el._myifInstance );
    } );
    return instances;
  }
}

export default MyIF;
